/**
 * Modal de escáner: abre la cámara para leer códigos QR y de barras y
 * rellenar campos del formulario de reserva.
 *
 * Comportamiento: la cámara NUNCA se cierra sola.
 *  - Va detectando códigos y los va acumulando en una lista (aunque sea uno).
 *  - Cada código se confirma en 2 lecturas antes de listarlo (anti-falsos).
 *  - Una vez detectado, se queda en la lista aunque deje de verse un instante
 *    (habitual en tarjetas con dos códigos juntos).
 *  - El usuario elige tocando el código correcto; solo entonces se cierra.
 *
 * Motor:
 *  - API nativa `BarcodeDetector` cuando existe (Android Chrome): acelerada
 *    por el sistema, detecta sobre el propio vídeo.
 *  - Fallback a `barcode-detector/ponyfill` (zxing-wasm) en iOS Safari, etc.,
 *    recortando el centro del fotograma (ROI) a un canvas reducido.
 * El .wasm se sirve empaquetado localmente (sin CDN externo).
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { X, QrCode } from 'lucide-react';
import styles from './ScannerModal.module.css';

interface ScannerModalProps {
    onClose: () => void;
    onScanned: (data: string) => void;
}

const BARCODE_FORMATS = [
    'qr_code', 'ean_13', 'ean_8', 'code_128', 'code_39', 'code_93',
    'upc_a', 'upc_e', 'pdf417', 'data_matrix', 'aztec', 'itf', 'codabar',
] as const;

// Solo se analiza el interior del recuadro visible. Recortamos exactamente esa
// región del fotograma (a resolución nativa) y la enviamos al detector: menos
// píxeles = más rápido, y no se cuelan códigos del fondo.
// MAX_SIDE acota el coste sin encoger de más (las barras siguen nítidas).
const MAX_SIDE = 1600;

// Recuadro rectangular (más ancho que alto): cómodo para códigos de barras 1D
// y suficiente para QR. Se usa igual para dibujarlo y para recortar.
function computeBox(): { w: number; h: number } {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 360;
    const w = Math.min(Math.round(vw * 0.9), 460);
    const h = Math.round(w * 0.66);
    return { w, h };
}

// Un código debe leerse 2 veces antes de añadirlo a la lista: evita falsos
// positivos. Nunca se acepta automáticamente; el usuario elige tocándolo.
const CONFIRM_COUNT = 2;
// Máximo de códigos mostrados a la vez.
const MAX_CODES = 6;

type DetectorBundle = { detector: unknown; isNative: boolean };

async function createBarcodeDetector(): Promise<DetectorBundle> {
    if ('BarcodeDetector' in globalThis) {
        const NativeBD = (globalThis as Record<string, unknown>).BarcodeDetector as new (opts: { formats: string[] }) => unknown;
        return {
            detector: new NativeBD({ formats: [...BARCODE_FORMATS] }),
            isNative: true,
        };
    }
    const mod = await import('barcode-detector/ponyfill');
    const wasmUrl = (await import('zxing-wasm/reader/zxing_reader.wasm?url')).default;
    mod.setZXingModuleOverrides({ locateFile: () => wasmUrl });
    const BarcodeDetector = mod.BarcodeDetector as
        | (new (opts: { formats: string[] }) => unknown)
        | undefined;
    if (typeof BarcodeDetector !== 'function') {
        throw new Error('BarcodeDetector (ponyfill) no está disponible en este entorno');
    }
    return {
        detector: new BarcodeDetector({ formats: [...BARCODE_FORMATS] }),
        isNative: false,
    };
}

export default function ScannerModal({ onClose, onScanned }: ScannerModalProps) {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const detectorRef = useRef<any>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const rafRef = useRef<number>(0);
    const rvfcRef = useRef<number>(0);
    const nativeBarcodeRef = useRef(false);
    const mountedRef = useRef(true);
    const acceptedRef = useRef(false);
    // Conteo de lecturas por valor aún NO confirmado (para el anti-falsos).
    const pendingRef = useRef<Map<string, number>>(new Map());
    // Valores ya confirmados y añadidos a la lista (no se repiten).
    const confirmedRef = useRef<Set<string>>(new Set());

    const [box, setBox] = useState(computeBox);
    // Espejo del recuadro para leerlo dentro del bucle sin recrearlo.
    const boxRef = useRef(box);

    const [detectedCodes, setDetectedCodes] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [cameraReady, setCameraReady] = useState(false);

    useEffect(() => { boxRef.current = box; }, [box]);

    useEffect(() => {
        const onResize = () => setBox(computeBox());
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('orientationchange', onResize);
        };
    }, []);

    const stopCamera = useCallback(() => {
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = 0;
        }
        const video = videoRef.current;
        if (video && rvfcRef.current && 'cancelVideoFrameCallback' in video) {
            video.cancelVideoFrameCallback(rvfcRef.current);
            rvfcRef.current = 0;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
        if (video) {
            video.srcObject = null;
        }
    }, []);

    const acceptCode = useCallback((value: string) => {
        if (acceptedRef.current) return;
        acceptedRef.current = true;
        try { navigator.vibrate?.(60); } catch { /* sin vibración */ }
        stopCamera();
        onScanned(value);
        onClose();
    }, [onScanned, onClose, stopCamera]);

    const scanLoop = useCallback(() => {
        const scheduleNext = () => {
            if (!mountedRef.current || acceptedRef.current) return;
            const video = videoRef.current;
            if (video && 'requestVideoFrameCallback' in video) {
                rvfcRef.current = video.requestVideoFrameCallback(() => void run());
            } else {
                rafRef.current = requestAnimationFrame(() => void run());
            }
        };

        const run = async () => {
            if (!mountedRef.current || acceptedRef.current) return;
            const video = videoRef.current;
            const detector = detectorRef.current;
            if (!video || !detector || video.readyState < 2) {
                scheduleNext();
                return;
            }
            try {
                const canvas = canvasRef.current;
                const ctx = canvas?.getContext('2d', { willReadFrequently: true });
                const vw = video.videoWidth;
                const vh = video.videoHeight;
                if (!canvas || !ctx || vw < 16 || vh < 16) {
                    scheduleNext();
                    return;
                }

                // Mapea el recuadro visible (CSS px sobre el vídeo con
                // object-fit: cover) a coordenadas reales del fotograma, para
                // analizar EXACTAMENTE lo que el usuario ve dentro del marco.
                const cw = video.clientWidth || vw;
                const ch = video.clientHeight || vh;
                const coverScale = Math.max(cw / vw, ch / vh);
                const { w: boxW, h: boxH } = boxRef.current;
                let sw = Math.min(vw, boxW / coverScale);
                let sh = Math.min(vh, boxH / coverScale);
                sw = Math.max(16, sw);
                sh = Math.max(16, sh);
                const sx = (vw - sw) / 2;
                const sy = (vh - sh) / 2;

                // No encogemos por debajo de MAX_SIDE: mantiene nítidas las barras.
                const scale = Math.min(1, MAX_SIDE / Math.max(sw, sh));
                const targetW = Math.round(sw * scale);
                const targetH = Math.round(sh * scale);
                if (canvas.width !== targetW || canvas.height !== targetH) {
                    canvas.width = targetW;
                    canvas.height = targetH;
                }
                ctx.drawImage(video, sx, sy, sw, sh, 0, 0, targetW, targetH);

                const barcodes: { rawValue?: string }[] = await detector.detect(canvas);
                if (!mountedRef.current || acceptedRef.current) return;

                // Lecturas de este fotograma (deduplicadas).
                const frameValues = new Set<string>();
                for (const b of barcodes) {
                    if (b.rawValue) frameValues.add(b.rawValue);
                }

                const pending = pendingRef.current;
                const confirmed = confirmedRef.current;
                const nuevos: string[] = [];
                for (const value of frameValues) {
                    if (confirmed.has(value)) continue; // ya está en la lista
                    const count = (pending.get(value) ?? 0) + 1;
                    if (count >= CONFIRM_COUNT) {
                        pending.delete(value);
                        confirmed.add(value);
                        nuevos.push(value);
                    } else {
                        pending.set(value, count);
                    }
                }

                if (nuevos.length) {
                    try { navigator.vibrate?.(40); } catch { /* sin vibración */ }
                    // Los códigos se acumulan y se mantienen (no desaparecen aunque
                    // dejen de verse). La cámara nunca se cierra sola.
                    setDetectedCodes(prev => {
                        const merged = [...prev];
                        for (const v of nuevos) {
                            if (!merged.includes(v)) merged.push(v);
                        }
                        return merged.length > MAX_CODES ? merged.slice(merged.length - MAX_CODES) : merged;
                    });
                }
            } catch { /* fotograma no legible; se reintenta */ }
            scheduleNext();
        };

        void run();
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        acceptedRef.current = false;
        pendingRef.current.clear();
        confirmedRef.current.clear();

        const init = async () => {
            try {
                const { detector, isNative } = await createBarcodeDetector();
                detectorRef.current = detector;
                nativeBarcodeRef.current = isNative;

                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: 'environment',
                        width: { ideal: 1920, min: 1280 },
                        height: { ideal: 1080, min: 720 },
                    },
                    audio: false,
                });
                if (!mountedRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
                streamRef.current = stream;

                const track = stream.getVideoTracks()[0];
                if (track) {
                    // Autoenfoque continuo: clave para leer códigos de barras 1D.
                    const capabilities = track.getCapabilities?.() as MediaTrackCapabilities & { focusMode?: string[] };
                    if (capabilities?.focusMode?.includes?.('continuous')) {
                        track.applyConstraints({
                            advanced: [{ focusMode: 'continuous' } as MediaTrackConstraintSet],
                        } as MediaTrackConstraints).catch(() => {});
                    }
                }

                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.onloadedmetadata = () => {
                        videoRef.current?.play().catch(() => {});
                        if (mountedRef.current) {
                            setCameraReady(true);
                            scanLoop();
                        }
                    };
                }
                setError(null);
            } catch (e: unknown) {
                if (mountedRef.current) {
                    setError(
                        (e instanceof Error ? e.message : null) ||
                        'No se pudo acceder a la cámara. Asegúrate de estar usando HTTPS y haber dado permisos.'
                    );
                }
            }
        };
        init();
        return () => { mountedRef.current = false; stopCamera(); detectorRef.current = null; };
    }, [scanLoop, stopCamera]);

    const handleSelectCode = (code: string) => { acceptCode(code); };

    const handleRemoveCode = (code: string) => {
        confirmedRef.current.delete(code);
        pendingRef.current.delete(code);
        setDetectedCodes(prev => prev.filter(c => c !== code));
    };

    const instruccion = detectedCodes.length === 0
        ? 'Apunta al código'
        : detectedCodes.length === 1
            ? 'Toca el código para usarlo'
            : 'Toca el código correcto';

    return (
        <div className={styles.backdrop}>
            <div className={styles.container}>
                <div className={styles.videoContainer}>
                    <video ref={videoRef} autoPlay playsInline muted />
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                </div>

                <div className={styles.overlay}>
                    <div className={styles.header}>
                        <button className={styles.iconButton} onClick={onClose} aria-label="Cerrar escáner"><X color="white" size={28} /></button>
                    </div>

                    <div className={styles.centerArea}>
                        {!cameraReady && !error && (
                            <div className={styles.loadingContainer}><span className={styles.loadingText}>Iniciando cámara...</span></div>
                        )}
                        {cameraReady && !error && (
                            <div className={styles.viewfinderContainer}>
                                <div className={styles.viewfinder} style={{ width: box.w, height: box.h }}>
                                    <div className={`${styles.corner} ${styles.cornerTopLeft}`} />
                                    <div className={`${styles.corner} ${styles.cornerTopRight}`} />
                                    <div className={`${styles.corner} ${styles.cornerBottomLeft}`} />
                                    <div className={`${styles.corner} ${styles.cornerBottomRight}`} />
                                </div>
                                <p className={styles.instructionText}>{instruccion}</p>
                            </div>
                        )}
                    </div>

                    <div className={styles.footer}>
                        {error && <div className={styles.errorContainer}><p className={styles.errorText}>{error}</p></div>}
                        {detectedCodes.length > 0 && (
                            <div className={styles.bubblesContainer}>
                                {detectedCodes.map((code, index) => (
                                    <div key={`${code}-${index}`} className={styles.pillWrapper}>
                                        <button className={styles.pillBubble} onClick={() => handleSelectCode(code)}>
                                            <div className={styles.pillIconContainer}><QrCode size={16} color="white" /></div>
                                            <span className={styles.pillText}>{code}</span>
                                        </button>
                                        <button
                                            className={styles.pillRemoveButton}
                                            onClick={() => handleRemoveCode(code)}
                                            aria-label="Descartar código"
                                        >
                                            <X size={14} color="rgba(255,255,255,0.7)" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
